import { useCallback } from 'react';

interface FormatOperation {
	syntax: string;
	selectedText: string;
	editableRef: React.RefObject<HTMLDivElement | null>;
}

export const useEditorFormatting = () => {
	const executeCommand = (command: string, value?: string): boolean => {
		const doc = document as any;
		if (typeof doc.execCommand === 'function') {
			return doc.execCommand(command, false, value);
		}
		return false;
	};

	const applyFormat = useCallback(({ syntax, editableRef }: FormatOperation): boolean => {
		if (!editableRef.current) return false;

		editableRef.current.focus();

		try {
			let success = false;

			switch (syntax) {
				case 'bold':
					success = executeCommand('bold');
					break;

				case 'italic':
					success = executeCommand('italic');
					break;

				case 'ul':
					success = executeCommand('insertUnorderedList');
					break;

				case 'ol':
					success = executeCommand('insertOrderedList');
					break;

				case 'h1':
					success = executeCommand('formatBlock', '<h2>');
					break;

				case 'h2':
					success = executeCommand('formatBlock', '<h3>');
					break;

				default:
					return false;
			}

			return success;
		} catch (error) {
			console.error(error);
			return false;
		}
	}, []);

	const clearFormat = useCallback(
		(editableRef: React.RefObject<HTMLDivElement | null>): boolean => {
			if (!editableRef.current) return false;
			editableRef.current.focus();

			const success = executeCommand('removeFormat');
			executeCommand('formatBlock', '<p>');

			return success;
		},
		[]
	);

	const indent = useCallback((editableRef: React.RefObject<HTMLDivElement | null>): boolean => {
		if (!editableRef.current) return false;
		editableRef.current.focus();

		return executeCommand('indent');
	}, []);

	const outdent = useCallback((editableRef: React.RefObject<HTMLDivElement | null>): boolean => {
		if (!editableRef.current) return false;
		editableRef.current.focus();

		return executeCommand('outdent');
	}, []);

	return {
		applyFormat,
		clearFormat,
		indent,
		outdent,
	};
};
